import { dNotify } from 'packages/lib/common-ui/notification/deduped-notify';
import {
  SampleGroup,
  sampleGroupZod,
} from 'packages/lib/api/annotations/contract/sample-group';
import {
  Task,
  taskCountRespZod,
  tasksRespZod,
} from 'packages/lib/api/annotations/contract/task';
import { apiGet } from 'packages/lib/api/annotations/rest';
import z from 'zod';
import {
  TaskGroup,
  taskGroupZod,
} from 'packages/lib/api/annotations/contract/task-group';
import {
  AbstractAnnotationStates,
  mapRoleToAnnotationStates,
} from 'packages/task-man/service/state-machine';
import { Role } from 'packages/task-man/constants';
import {
  Instruction,
  instructionZod,
} from 'packages/lib/api/annotations/contract/instruction';
import {
  TaxonomyInstruction,
  taxonomyInstructionRespZod,
} from 'packages/lib/api/annotations/contract/taxonomy';
import {
  Category,
  CategoriesResp,
  categoriesZod,
} from 'packages/lib/api/annotations/contract/category';
import {
  Attribute,
  AttributesResp,
  attributesRespZod,
} from 'packages/lib/api/annotations/contract/attribute';
import retry from 'async-retry';
import { isCollisionError } from 'packages/lib/utils/error';
import * as Sentry from '@sentry/react';
import { TaskGroupType } from 'packages/lib/api/annotations/contract/enums';
import { pickupManyTasks } from 'packages/task-man/service/edit';
import { TaskHasInvalidObjectTokenError } from 'packages/lib/utils/error';
import {
  taskHasObjectToken,
  getTaskObjectToken,
} from 'packages/task-man/service/utilities';
import { isSceneEnrichmentInSingleTaskMode } from 'packages/at-shell/utils/is-scene-enrichment-single-task-schema';
import { roleStateMap } from 'packages/task-man/service/state-machine/utilities';
import {
  DEFAULT_LABELING_PICKUP_STRATEGY,
  DEFAULT_QA_AUDIT_PICKUP_STRATEGY,
  PickupStrategy,
  User,
} from 'packages/lib/api/annotations-ops/contract';
import { usersApi, taskManApi } from 'packages/lib/api/annotations-ops/clients';
import { applyPickupStrategy } from 'packages/task-man/service/pickup/apply-pickup-strategy';

// ANNOT-3113: Users want some sample groups to be annotated in random order
export const DEFAULT_BATCH_SIZE = 20; // default multi-task batch size

export type Classification = Category & {
  attributes: Record<string, Attribute[]>;
  defaultAttributes: Record<string, Attribute>;
};

/**
 * Task Queue abstraction describing Task Queue behavior and actions.
 * This should be invoked when implementing app interfaces that allow
 * end-users to pickup tasks.
 *
 * Used for both single-task and multi-task/linked-task workflows.
 */
export interface TaskQueue {
  taskGroupType: TaskGroupType;
  userEmail: string;
  role: Role;
  states: AbstractAnnotationStates;
  sampleGroup: SampleGroup; // query via instructionToken
  taskGroup: TaskGroup; // query via taskGroup.sampleGroupToken; or query via task.taskGroupToken
  instruction: Instruction; // get token from taskGroup.instructionToken
  taxonomyInstruction: TaxonomyInstruction; // get via taxonomy.instructions[number].name === instruction.name
  classifications: Classification[];
  user: User | null;

  /**
   * Single-task AND Multi-task workflow
   * Originally the plan was to create separate methods for single vs multi, but ended up using common methods for both
   */
  /** get up to N tasks that might already be assigned to the user. Use n=1 for single-task workflows */
  getManyAssignedTasks: (n: number) => Promise<Task[]>;
  /** get all tasks (in the TaskGroup for this TaskQueue) that contain this objectToken in their tag field (ignoring their assignment) */
  getTasksByObjectToken: (objectToken: string) => Promise<Task[]>;
  /** fetch and peek at next N unassigned tasks. Use n=1 for single-task workflows */
  peekUnassigned: (n: number) => Promise<Task[]>;
  /** peek next N unassigned task(s), assign them and increment their annotation state. Use n=1 for single-task workflows */
  feedUnassigned: (n: number, retries?: number) => Promise<Task[]>;
  /** assigns tasks and increments their annotation state. Similar to feedUnassigned but processes tasks passed in via param  */
  assignTasks: (tasks: Task[], retries?: number) => Promise<Task[]>;
  /** get task count for this task group
   * @param includeInProgressStates If true, includes all task InProgress states in the count. Otherwise, only includes initial task states. Defaults to true
   */
  count: (
    which?: 'all' | 'assigned' | 'unassigned',
    includeInProgressStates?: boolean
  ) => Promise<number>;
}

export type TaskQueueParams = Pick<
  TaskQueue,
  'role' | 'userEmail' | 'taskGroupType'
> & {
  sampleGroupToken: string;
  allClassifications?: CategoriesResp;
  allAttributes?: Attribute[];
  allTaxonomyInstructions?: TaxonomyInstruction[];
  loadTasksByObjectToken?: boolean; // Will be true for Scene Enrichment, false for Classification Enrichment
  objectToken?: string; // Possibly present when using Scene Enrichment (depending on whether in URL), undefined otherwise
};

/**
 * Task Queue factory function.
 *
 * Use this when implementing app interfaces that allow
 * end-users to pickup tasks. See also: related react hooks.
 */
export const taskQueue = async (
  params: TaskQueueParams
): Promise<TaskQueue> => {
  const { sampleGroupToken, userEmail, role, taskGroupType } = params;
  const states = mapRoleToAnnotationStates(role) ?? roleStateMap['label']; // default to label role states if role is undefined for some reason
  const { initial, inProgress } = states;
  const { sampleGroup, taskGroup, instructionToken } = await getTaskQueueGroups(
    sampleGroupToken,
    taskGroupType
  );
  const { instruction, taxonomyInstruction } = await getInstructionData(
    instructionToken,
    params.allTaxonomyInstructions
  );
  const { classifications } = await getClassificationData(
    taxonomyInstruction,
    params.allClassifications,
    params.allAttributes
  );

  const user = await usersApi.getCurrent();

  // Used by both single-task (n=1) and multitask workflows
  const getManyAssignedTasks = (n = DEFAULT_BATCH_SIZE) =>
    apiGet(
      `/tasks?taskGroupToken=${taskGroup.token}&assignee=${userEmail}&annotationState=${inProgress}&orderBy=createdAt&pageSize=${n}`,
      tasksRespZod.parse
    );

  // Used by both single-task (n=1) and multitask workflows
  const peekUnassigned = async (n = DEFAULT_BATCH_SIZE) => {
    return params.loadTasksByObjectToken
      ? peekManyLinkedTasksInternal()
      : peekUnassignedInternal(n);
  };

  // Used by both single-task (n=1) and multitask workflows
  const peekUnassignedInternal = async (
    n = DEFAULT_BATCH_SIZE
  ): Promise<Task[]> => {
    let strategy: PickupStrategy = DEFAULT_LABELING_PICKUP_STRATEGY;
    if (role === 'qa' || role === 'audit') {
      strategy = DEFAULT_QA_AUDIT_PICKUP_STRATEGY;

      const strat = await taskManApi.queue
        .getPickupStrategy(sampleGroupToken)
        .catch(() => null);
      if (strat) {
        strategy = strat;
      }
    }

    const pickupParams = {
      taskGroupToken: taskGroup.token,
      annotationState: initial,
      userEmail,
      n,
      strategy,
    };

    console.log('[Pickup Strategy] Attempting task pickup with params: ', {
      pickupParams,
    });

    const result = await applyPickupStrategy(pickupParams);

    console.log(`[Pickup Strategy] Found ${result.length}/${n} eligible tasks`);

    return result;
  };

  /** See ANNOT-2533 for requirements */
  const peekManyLinkedTasksInternal = async (): Promise<Task[]> => {
    // NOTE: Case 1: an object_token is in the URL. If so, use it to get unassigned tasks by that token.
    // (Note that code in linked-task-validator-effect handles cases such as tasks related to this objectToken that aren't assigned and need to be)
    if (params.objectToken) {
      return getUnassignedToDoTasksByObjectTokenInternal(params.objectToken);
    }

    // NOTE: Case 2: an object_token is NOT in the URL. So load the first unassigned task, use it to get an objectToken, and load all tasks for that token.

    // STEP 2A: get first set of tasks, which we will use to get an objectToken from the task.tag field
    const unassignedTasks = await getUnassignedToDoTasksInternal({
      pageNo: 1,
      pageSize: 2,
    });
    if (unassignedTasks.length === 0) return [];

    // eslint-disable-next-line prefer-destructuring
    const task = unassignedTasks[0];

    // STEP 2B: get the objectToken from task.tag
    // Per Edvin/Allen, the task.tag will ALWAYS be prefixed with "objectToken." for the queues we're using
    if (taskHasObjectToken(task)) {
      const objectToken = getTaskObjectToken(task);
      // STEP 2C: Fetch all unassigned ToDo tasks associated with that objectToken
      return await getUnassignedToDoTasksByObjectTokenInternal(objectToken);
    }

    // Bug fix attempt: new Scene Enrichment had tasks getting flagged as having no objectToken. Attempt to avoid that here.
    if (isSceneEnrichmentInSingleTaskMode(task)) {
      return [];
    }

    throw new TaskHasInvalidObjectTokenError(
      `Error: Task for linked-task queue did not have an object token in its tag field`,
      task
    );
  };

  const getUnassignedToDoTasksInternal = async (params: {
    pageNo: number;
    pageSize: number;
  }): Promise<Task[]> => {
    return apiGet(
      `/tasks?taskGroupToken=${taskGroup.token}&assignee=%null&pageSize=${params.pageSize}&annotationState=${initial}&pageNo=${params.pageNo}&orderBy=createdAt`,
      tasksRespZod.parse
    );
  };

  const getUnassignedToDoTasksByObjectTokenInternal = async (
    objectToken: string
  ): Promise<Task[]> => {
    // Note that we intentionally omit the annotationState param, as the Scene Enrichment tool will later update the annotationState param with
    // the current task state if it is different. Per Edvin: leaving taskGroupToken in this query helps speed it up and ensure it's safer.
    return await apiGet(
      `/tasks?taskGroupToken=${taskGroup.token}&assignee=%null&annotationState=${initial}&pageSize=1000&pageNo=1&tag=objectToken.${objectToken}`,
      tasksRespZod.parse
    );
  };

  /** Returns ALL tasks linked by this objectToken, regardless of annotation state or assignee status.
   * Callers can then use this to validate that ALL tasks belonging to an object token are assigned to the current user and in the same annotation state
   */
  const getTasksByObjectToken = (objectToken: string) =>
    apiGet(
      `/tasks?taskGroupToken=${taskGroup.token}&pageSize=1000&pageNo=1&tag=objectToken.${objectToken}`,
      tasksRespZod.parse
    );

  // Used by both single-task (n=1) and multitask workflows
  const feedUnassigned = async (n = DEFAULT_BATCH_SIZE, retries = 3) => {
    /**
     * Retry up to 3 times in the case of failure due to task assignment collision.
     */
    return retry(
      async (bail) => {
        try {
          const unassignedTasks = await peekUnassigned(n);
          return assignTasks(unassignedTasks);
        } catch (err) {
          // Only retry if there is a collision error
          if (!isCollisionError(err)) {
            bail(err as Error);
            return [];
          }
          throw err; // tell retry() to do next attempt
        }
      },
      {
        retries,
        minTimeout: 0,
        onRetry: (err, attempt) => {
          // report error to sentry if collision occurs more than maxRetries
          if (isCollisionError(err) && attempt > retries - 1) {
            err.message = `${err.message} (${attempt})`;
            Sentry.captureException(err, {
              tags: {
                sample_group: sampleGroup.name,
                user_task_man_role: role,
              },
            });
          }
        },
      }
    );
  };

  // Used by both single-task (n=1) and multitask workflows
  // Like feedUnassigned, but picks up the tasks passed in as a param, instead of peeking them from the Sample Group
  const assignTasks = async (unassignedTasks: Task[]) => {
    return await pickupManyTasks(unassignedTasks, userEmail);
  };

  const count = async (
    which: 'all' | 'unassigned' | 'assigned' = 'all',
    includeInProgressStates = true
  ) => {
    let commonQueryParams = `?taskGroupToken=${taskGroup.token}&partner=None`;
    const taskStatesToInclude =
      initial + (includeInProgressStates ? ',' + inProgress : '');
    commonQueryParams += `&annotationState=${taskStatesToInclude}`;

    if (which === 'all') {
      const [assignedTaskCount, unassignedTaskCount] = await Promise.all([
        apiGet(
          `/tasks/count${commonQueryParams}&assignee=${userEmail}`,
          taskCountRespZod.parse
        ),
        apiGet(
          `/tasks/count${commonQueryParams}&assignee=%null`,
          taskCountRespZod.parse
        ),
      ]);
      return unassignedTaskCount['*'] + assignedTaskCount['*'];
    }

    if (which === 'assigned') {
      const assignedTaskCount = await apiGet(
        `/tasks/count${commonQueryParams}&assignee=${userEmail}`,
        taskCountRespZod.parse
      );
      return assignedTaskCount['*'];
    }

    const unassignedTaskCount = await apiGet(
      `/tasks/count${commonQueryParams}&assignee=%null`,
      taskCountRespZod.parse
    );
    return unassignedTaskCount['*'];
  };

  return {
    taskGroupType,
    userEmail,
    role,
    states,
    sampleGroup,
    taskGroup,
    instruction,
    taxonomyInstruction,
    classifications,
    user,
    getManyAssignedTasks,
    getTasksByObjectToken,
    peekUnassigned,
    feedUnassigned,
    assignTasks,
    count,
  };
};

export const getAllClassificationData = async () => {
  const allTaxonomyInstructions = await apiGet(
    '/taxonomy/instructions',
    taxonomyInstructionRespZod.parse
  ).catch((err) => {
    dNotify.error({
      message: `Failed to fetch all data: /taxonomy/instructions`,
    });
    throw err;
  });
  const allClassifications = await apiGet<CategoriesResp>(
    '/categories?pageSize=1000',
    categoriesZod.parse
  ).catch((err) => {
    dNotify.error({
      message: `Failed to fetch all data: /categories`,
    });
    throw err;
  });
  const allAttributes = await apiGet<AttributesResp>(
    '/attributes',
    attributesRespZod.parse
  ).catch((err) => {
    dNotify.error({
      message: `Failed to fetch all data: /attributes`,
    });
    throw err;
  });
  return { allClassifications, allAttributes, allTaxonomyInstructions };
};

export const getClassificationData = async (
  taxonomyInstruction: TaxonomyInstruction,
  allCategoriesVal?: CategoriesResp,
  allAttributesVal?: AttributesResp
) => {
  if (taxonomyInstruction.type === 'videotag') {
    /**
     * VideoEvent tasks don't need to use `classifications` data.
     * see: https://confluence.ci.motional.com/confluence/display/ML/Annotation+Independence#:~:text=%7D-,How%20it%20works,-Annotators%20should%20be
     */
    return { classifications: [] };
  }
  const { categories: taxonomyCategories, attributes: taxonomyAttributes } =
    taxonomyInstruction;
  const { allCategories, allAttributes } = await (async () => {
    if (allCategoriesVal && allAttributesVal) {
      return {
        allCategories: allCategoriesVal,
        allAttributes: allAttributesVal,
      };
    }
    const [allCats, allAtts] = await Promise.all([
      apiGet<CategoriesResp>('/categories?pageSize=1000', categoriesZod.parse),
      apiGet<AttributesResp>('/attributes', attributesRespZod.parse),
    ]);
    return { allCategories: allCats, allAttributes: allAtts };
  })();
  const filteredCategories = Object.entries(taxonomyCategories).filter(
    (c) => c[0] !== 'hole'
  );
  const classifications = filteredCategories.map<
    Category & {
      attributes: Record<string, Attribute[]>;
      defaultAttributes: Record<string, Attribute>;
    }
  >(([categoryName, attributeNames]) => {
    const category = allCategories.find((c) => c.name === categoryName);
    if (!category) {
      throw new Error(`Category ${categoryName} not found`);
    }

    const attributes = {} as Record<string, Attribute[]>;
    const defaultAttributes = {} as Record<string, Attribute>;

    attributeNames.forEach((attributeName) => {
      const attributeValues = taxonomyAttributes[attributeName];
      if (!attributeValues) {
        throw new Error(
          `Attribute ${attributeName} has no associated values for category ${categoryName}`
        );
      }
      if (!attributeValues.length) {
        // this attribute has no options, so return early
        return;
      }
      const attributeOptionNames = attributeValues.map(
        (value) => `${attributeName}.${value}`
      );
      const attributeOpts = allAttributes.filter((a) =>
        attributeOptionNames.includes(a.name)
      );
      if (!attributeOpts) {
        throw new Error(
          `Attribute ${attributeName} has no associated values for category ${categoryName}`
        );
      }
      attributes[attributeName] = attributeOpts;
      const defaultAttributeOption = attributeOpts.find((opt) =>
        opt.name.includes(attributeValues[0])
      );
      if (!defaultAttributeOption) {
        throw new Error(
          `Default attribute option for ${attributeName} not found`
        );
      }
      defaultAttributes[attributeName] = defaultAttributeOption; // Store the token as the default value
    });
    return {
      ...category,
      attributes,
      defaultAttributes,
    };
  });
  return { classifications };
};

export const getInstructionData = async (
  instructionToken: string,
  allTaxonomyInstructions?: TaxonomyInstruction[]
) => {
  const instruction = await apiGet(
    `/instructions/${instructionToken}`,
    instructionZod.parse
  );
  const taxonomyInstructions = await (async () => {
    if (allTaxonomyInstructions) return allTaxonomyInstructions;
    return apiGet('/taxonomy/instructions', taxonomyInstructionRespZod.parse);
  })();
  const taxonomyInstruction = taxonomyInstructions?.find(
    ({ name }) => name === instruction.name
  );
  if (!taxonomyInstruction) {
    throw new Error(
      'The given task queue does not have an associated Taxonomy Instruction'
    );
  }
  return { instruction, taxonomyInstruction };
};

export const getTaskQueueGroups = async (
  sampleGroupToken: string,
  taskGroupType: TaskGroupType
) => {
  const [sampleGroup, taskGroups] = await Promise.all([
    apiGet(`/sample-groups/${sampleGroupToken}`, sampleGroupZod.parse),
    apiGet(
      `/task-groups?sampleGroupToken=${sampleGroupToken}&type=${taskGroupType}`,
      z.array(taskGroupZod).parse
    ),
  ]);
  /**
   * NOTE: assume 1 taskGroup per sampleGroup
   */
  if (taskGroups.length !== 1) {
    throw new Error(
      `SampleGroups and TaskGroups are assumed to have 1:1 relationship. This SampleGroup has ${taskGroups.length} TaskGroups.`
    );
  }
  const [taskGroup] = taskGroups;
  const [task] = await apiGet(
    `/tasks?taskGroupToken=${taskGroup.token}&pageSize=1`,
    tasksRespZod.parse
  );
  if (!task) {
    throw new Error(
      `The given TaskGroup for this task queue has no associated tasks.`
    );
  }
  const { instructionToken } = task;
  if (!instructionToken) {
    throw new Error(
      `The given Task does not have an associated Instruction Token.`
    );
  }
  return { sampleGroup, taskGroup, instructionToken };
};
